import { Controller } from "@hotwired/stimulus"
import { get } from "@rails/request.js"

// Performs a debounced phone number lookup as the user types.
// Shows whether the phone matches an existing prospect or is new.
//
// Targets:
//   - input: the phone number input field
//   - result: container for lookup status display
//   - existingFields: shown when an existing prospect is found
//   - newFields: shown when phone is valid but no prospect found
//   - prospectUlid: hidden input for the matched prospect's ULID
//   - prospectMode: hidden input for "existing" or "new"
//   - prospectName: element displaying matched prospect name
//   - phoneField: the phone field inside newFields (to pre-fill)
//
// Values:
//   - url: the phone lookup endpoint URL

export default class extends Controller {
  static targets = [
    "input",
    "result",
    "existingFields",
    "newFields",
    "prospectUlid",
    "prospectMode",
    "prospectName",
    "phoneField",
  ]

  static values = {
    url: String,
  }

  connect() {
    this.timeout = null

    // If input already has a value (e.g., URL param pre-fill), trigger lookup
    if (this.inputTarget.value.trim()) {
      this.fetchLookup()
    }
  }

  disconnect() {
    if (this.timeout) clearTimeout(this.timeout)
  }

  lookup() {
    if (this.timeout) clearTimeout(this.timeout)
    this.timeout = setTimeout(() => this.fetchLookup(), 500)
  }

  async fetchLookup() {
    const phone = this.inputTarget.value.trim()

    if (!phone) {
      this.reset()
      return
    }

    try {
      const response = await get(this.urlValue, {
        query: { phone_number: phone },
        responseKind: "json",
      })

      if (!response.ok) return

      const data = await response.json
      this.handleResult(data)
    } catch {
      // silently fail — lookup is non-critical
    }
  }

  handleResult(data) {
    if (data.found === true) {
      this.showExistingProspect(data.prospect)
    } else if (data.found === false) {
      this.showNewProspect(data.formatted_phone)
    } else {
      this.reset()
    }
  }

  showExistingProspect(prospect) {
    this.resultTarget.innerHTML = this.matchHtml(prospect)
    this.resultTarget.classList.remove("hidden")

    this.prospectUlidTarget.value = prospect.ulid
    this.prospectModeTarget.value = "existing"

    if (this.hasExistingFieldsTarget)
      this.existingFieldsTarget.classList.remove("hidden")
    if (this.hasNewFieldsTarget) this.newFieldsTarget.classList.add("hidden")
    if (this.hasProspectNameTarget)
      this.prospectNameTarget.textContent = `${prospect.full_name} (${prospect.formatted_phone_number})`
  }

  showNewProspect(formattedPhone) {
    this.resultTarget.innerHTML = this.noMatchHtml(formattedPhone)
    this.resultTarget.classList.remove("hidden")

    this.prospectUlidTarget.value = ""
    this.prospectModeTarget.value = "new"

    if (this.hasExistingFieldsTarget)
      this.existingFieldsTarget.classList.add("hidden")
    if (this.hasNewFieldsTarget)
      this.newFieldsTarget.classList.remove("hidden")

    // Pre-fill the phone field in the new prospect section
    if (this.hasPhoneFieldTarget && !this.phoneFieldTarget.value) {
      this.phoneFieldTarget.value = this.inputTarget.value.trim()
    }
  }

  reset() {
    this.resultTarget.innerHTML = ""
    this.resultTarget.classList.add("hidden")

    this.prospectUlidTarget.value = ""
    this.prospectModeTarget.value = ""

    if (this.hasExistingFieldsTarget)
      this.existingFieldsTarget.classList.add("hidden")
    if (this.hasNewFieldsTarget) this.newFieldsTarget.classList.add("hidden")
  }

  matchHtml(prospect) {
    return `
      <div class="flex items-center gap-2 text-sm font-medium text-green-700 dark:text-green-400">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
        Existing prospect: ${this.escapeHtml(prospect.full_name)} (${this.escapeHtml(prospect.formatted_phone_number)})
      </div>
    `
  }

  noMatchHtml(formattedPhone) {
    return `
      <div class="flex items-center gap-2 text-sm font-medium text-blue-700 dark:text-blue-400">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
        No existing prospect with ${this.escapeHtml(formattedPhone)} — new prospect will be created
      </div>
    `
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
